import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import Flex from '@wix/da-ds/pkg/Flex';
import Loader from '@wix/da-ds/pkg/Loader';
import { useNormalizedDeviationStream } from '@wix/da-shared-react/pkg/utils/hooks/useStream';
import OnScrollBottom, {
  DISTANCE_UNIT,
} from '@wix/da-shared-react/pkg/OnScrollBottom';
import { Props } from './StashFolder';
import { STASH_FOLDER_DEVIATIONS_STREAM } from '../../../constants';
import { FolderListMobile } from '../../components/stash/FolderList';
import StudioThumb from '../../components/StudioThumb';
import FolderHeaderMobile from '../../components/stash/FolderHeaderMobile';
import StashTabsMobile, {
  TabType,
} from '../../components/stash/StashTabsMobile';

import s from './StashFolderMobile.scss';

const StashFolderMobile: FC<Props> = ({
  folder,
  subfolders,
  isFetchingFolders,
  fetchMoreResults,
  copyFolderLink,
}) => {
  const { streamId, streamConfig, streamThrottle } =
    STASH_FOLDER_DEVIATIONS_STREAM;
  const {
    items: deviations,
    isFetching,
    hasMore,
  } = useNormalizedDeviationStream(streamId, streamThrottle, streamConfig);

  const onShareClick = useCallback(
    () => copyFolderLink(folder),
    [copyFolderLink, folder]
  );

  const hasDeviations = deviations.length > 0;
  const hasSubfolders = subfolders && subfolders.length > 0;
  const [activeTab, setActiveTab] = useState(
    hasDeviations ? TabType.Files : hasSubfolders ? TabType.Folders : undefined
  );

  return (
    <Flex className={s['root']} direction="column">
      <FolderHeaderMobile folder={folder} onShareClick={onShareClick} />
      {hasDeviations && hasSubfolders && (
        <StashTabsMobile
          className={s['tabs']}
          activeTab={activeTab}
          onChange={(tab: TabType) => setActiveTab(tab)}
        />
      )}
      <BiLoggerContextProvider value={{ sectionname: activeTab }}>
        <Flex direction="column" gap={16}>
          {activeTab === TabType.Files && (
            <>
              <div className={s['thumbs']}>
                {deviations.map(deviation => (
                  <StudioThumb
                    key={`thumb-${deviation.deviationId}`}
                    className={s['thumb']}
                    deviation={deviation}
                  />
                ))}
              </div>
              {!isFetching && hasMore && (
                <OnScrollBottom
                  checkOnMount
                  checkOnUpdate={hasMore}
                  onScrolledToBottom={() => fetchMoreResults()}
                  distanceToBottom={100}
                  distanceUnit={DISTANCE_UNIT.PX}
                />
              )}
            </>
          )}
          {activeTab === TabType.Folders && (
            <div className={s['folders']}>
              <FolderListMobile />
            </div>
          )}

          {(isFetching || isFetchingFolders) && (
            <div className={s['loading']}>
              <Loader className={s['sticky']} state="loading" size="big" />
            </div>
          )}
        </Flex>
      </BiLoggerContextProvider>
    </Flex>
  );
};
StashFolderMobile.displayName = 'StashFolderMobile';

export default StashFolderMobile;
